/**
 * Block 12 PR-B Phase B3 Part C Step 13 — Russian ↔ English countertopKind mapping.
 *
 * Shared `domain/countertop-kinds-vocabulary.ts` defines vocabulary в Russian labels
 * (`'Столешницы ЛДСП' | 'Столешницы HPL' | 'Камень акрил' | 'Камень кварц' |
 * 'Литой камень'`) — это user-facing canonical value, хранится в `params.countertopKind`.
 * B2 formula `countertopQ` принимает English dispatch enum (`'laminated' | 'hpl' |
 * 'acrylic-stone' | 'quartz-stone' | 'cast-stone'`) — internal в formulas-layer.
 *
 * Этот helper мостит два naming-конвенции. JSON state хранит Russian, formula
 * dispatcher принимает English.
 *
 * Reference: `packages/shared/src/domain/countertop-kinds-vocabulary.ts` §«Naming
 * disambiguation» — ADR-0020 fixes split за сохранение B2 frozen module +
 * type-safety at compile.
 */

import { COUNTERTOP_KINDS, type CountertopKind, type CountertopKindLabel } from '@pomoshnik/shared';

/** Bidirectional map Russian label → English dispatch enum. */
const RU_TO_EN: Record<CountertopKindLabel, CountertopKind> = {
  'Столешницы ЛДСП': 'laminated',
  'Столешницы HPL': 'hpl',
  'Камень акрил': 'acrylic-stone',
  'Камень кварц': 'quartz-stone',
  'Литой камень': 'cast-stone',
};

export function countertopKindRussianToEnglish(label: CountertopKindLabel): CountertopKind {
  const en = RU_TO_EN[label];
  if (!en) {
    // Defense-in-depth: schema validation должна catch это раньше, но stale IDB
    // draft из старой версии app может содержать неизвестный label.
    throw new Error(`countertopKindRussianToEnglish: unknown label ${JSON.stringify(label)}`);
  }
  return en;
}

/** Russian unit label для UI рендера (соответствует B2 `countertopUnit`). */
export function countertopUnitRu(kind: CountertopKind): 'листов' | 'пм' {
  if (kind === 'laminated' || kind === 'hpl') return 'листов';
  return 'пм';
}

/**
 * Deeplink target по типу столешницы:
 *   - ЛДСП / HPL → Block 2 «Подбери декор» (есть в каталоге декоров)
 *   - Камень любой → Block 8 «Подрядчики» (камень делается на заказ)
 */
export function countertopDeeplink(kind: CountertopKind): 'decor' | 'partner' {
  if (kind === 'laminated' || kind === 'hpl') return 'decor';
  return 'partner';
}

/** Re-export для tests + caller convenience. */
export { COUNTERTOP_KINDS };
export type { CountertopKindLabel, CountertopKind };
